import React, { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface ComposeMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  senderId: number;
  onMessageSent?: () => void;
  defaultSubject?: string;
  defaultContent?: string;
}

interface Recipient {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
}

export const ComposeMessageModal: React.FC<ComposeMessageModalProps> = ({
  isOpen,
  onClose,
  senderId,
  onMessageSent,
  defaultSubject = '',
  defaultContent = ''
}) => {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selectedRecipientId, setSelectedRecipientId] = useState<number | null>(null);
  const [subject, setSubject] = useState(defaultSubject);
  const [messageContent, setMessageContent] = useState(defaultContent);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Load available recipients and initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      loadRecipients();
      // Initialize subject and content only when modal opens
      setSubject(defaultSubject);
      setMessageContent(defaultContent);
      setError('');
      setSuccess(false);
    }
  }, [isOpen]); // Only run when isOpen changes, not when defaults change

  const loadRecipients = async () => {
    setLoadingRecipients(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/messaging`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId: senderId,
          operation: 'load_recipients',
          data: {}
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      setRecipients(result.data?.recipients || []);
    } catch (error) {
      console.error('Error loading recipients:', error);
      setError('Failed to load recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!selectedRecipientId) {
      setError('Please select a recipient');
      return;
    }

    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }
    
    if (!messageContent.trim()) {
      setError('Message content is required');
      return;
    }
    
    if (subject.length > 255) {
      setError('Subject must be 255 characters or less');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/messaging`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId: senderId,
          operation: 'send_message',
          data: {
            senderId,
            recipientId: selectedRecipientId,
            subject: subject.trim(),
            body: messageContent.trim()
          }
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      // Trigger email notification for new message
      const insertedMessage = result.data?.message;
      if (insertedMessage?.id) {
        try {
          const { emailNotificationTriggers } = await import('../../lib/services/emailNotificationTriggers');
          await emailNotificationTriggers.triggerMessageNotification(insertedMessage.id);
        } catch (emailError) {
          console.error('Failed to trigger message email notification:', emailError);
          // Don't fail the message sending if email fails
        }
      }

      setSuccess(true);
      
      // Call callback if provided
      if (onMessageSent) {
        onMessageSent();
      }
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setSuccess(false);
        handleClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setSelectedRecipientId(null);
      setSubject(defaultSubject);
      setMessageContent(defaultContent);
      setError('');
      setSuccess(false);
      setSearchQuery('');
      onClose();
    }
  };

  if (!isOpen) return null;

  const filteredRecipients = recipients.filter(recipient => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      recipient.username.toLowerCase().includes(query) ||
      recipient.first_name?.toLowerCase().includes(query) ||
      recipient.last_name?.toLowerCase().includes(query) ||
      recipient.email?.toLowerCase().includes(query) ||
      recipient.phone_number?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-2xl mx-4 flex flex-col" style={{ maxHeight: 'calc(100vh - 4rem)', minHeight: 'auto' }}>
        <div className="flex items-center justify-between p-4 sm:p-6 pb-3 sm:pb-4 border-b border-gray-700 flex-shrink-0">
          <h3 className="text-white text-lg font-semibold">
            ✉️ Compose New Message
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-white disabled:opacity-50 text-xl"
          >
            ✕
          </button>
        </div>

        {success ? (
          <div className="text-center py-12 px-6">
            <div className="text-green-400 text-5xl mb-4">✅</div>
            <div className="text-white text-xl mb-2">Message Sent!</div>
            <div className="text-white text-sm">
              Your message has been delivered successfully
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ overscrollBehavior: 'contain' }}>
              <div className="space-y-4">
                {error && (
                  <div className="bg-red-600/20 border border-red-600/30 rounded-lg p-3">
                    <div className="text-red-400 text-sm">{error}</div>
                  </div>
                )}

                {/* Recipient Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    To: *
                  </label>
                  
                  {loadingRecipients ? (
                    <div className="text-white text-sm py-4 text-center">
                      Loading your team members...
                    </div>
                  ) : recipients.length === 0 ? (
                    <div className="bg-yellow-600/20 border border-yellow-600/30 rounded-lg p-4">
                      <div className="text-yellow-400 text-sm">
                        <strong>No referrals found.</strong>
                        <p className="mt-2">You can only send messages to your direct referrals. Start referring friends by sharing your referral link!</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Search Recipients */}
                      <input
                        type="text"
                        placeholder="Search team members..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-3 py-2 mb-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />

                      {/* Recipient Dropdown */}
                      <select
                        value={selectedRecipientId || ''}
                        onChange={(e) => setSelectedRecipientId(Number(e.target.value))}
                        disabled={isLoading}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                      >
                        <option value="">Select a team member...</option>
                        {filteredRecipients.map(recipient => {
                          const displayName = `${recipient.first_name || ''} ${recipient.last_name || ''}`.trim() || recipient.username;
                          const phoneNumber = recipient.phone_number ? ` • ${recipient.phone_number}` : '';
                          return (
                            <option key={recipient.id} value={recipient.id}>
                              {displayName} (@{recipient.username}){phoneNumber}
                            </option>
                          );
                        })}
                      </select>

                      {filteredRecipients.length === 0 && searchQuery && (
                        <div className="text-white text-sm mt-2">
                          No team members match your search.
                        </div>
                      )}

                      <div className="text-sm text-white mt-2">
                        {recipients.length} team member{recipients.length !== 1 ? 's' : ''} available
                      </div>
                    </>
                  )}
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Enter message subject..."
                    maxLength={255}
                    disabled={isLoading}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                  />
                  <div className="text-sm text-white mt-1">
                    {subject.length}/255 characters
                  </div>
                </div>

                {/* Message Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    placeholder="Type your message here..."
                    rows={8}
                    disabled={isLoading}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 resize-none"
                  />
                  <div className="text-sm text-white mt-1">
                    {messageContent.length} characters
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-4 sm:p-6 border-t border-gray-700 flex-shrink-0 bg-gray-800">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || !selectedRecipientId || !subject.trim() || !messageContent.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  '📤 Send Message'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

